/**
 * Accessible modal overlay: backdrop click, Escape, focus trap, focus restore.
 * Used by the Help Center and by confirmation dialogs.
 *
 * Sizing contract: the panel never exceeds the viewport (`100dvh` minus a
 * margin). The header and an optional `footer` stay put; everything between
 * them lives in `.overlay__scroll`, which is the ONE scroll container — so a
 * tall form at 1280×720 scrolls inside the dialog instead of being clipped
 * by the panel (the previous panel was `overflow: hidden` with no scroller,
 * which cut the Save button off on short screens).
 */
import { useCallback, useEffect, useRef, type ReactNode } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface OverlayDialogProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Rendered between the title and the close button (e.g. a tab list). */
  headerExtra?: ReactNode;
  /** Extra class on the panel (e.g. `overlay__panel--wide` for pickers). */
  panelClassName?: string;
  /** Pinned below the scrolling body (primary actions). */
  footer?: ReactNode;
}

export function OverlayDialog({
  title,
  onClose,
  children,
  headerExtra,
  panelClassName,
  footer,
}: OverlayDialogProps): JSX.Element {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  const focusables = useCallback((): HTMLElement[] => {
    const panel = panelRef.current;
    if (panel === null) return [];
    return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (element) => element.offsetParent !== null || element === document.activeElement
    );
  }, []);

  useEffect(() => {
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    const first = focusables()[0] ?? panelRef.current;
    first?.focus();
    return () => restoreFocusRef.current?.focus?.();
  }, [focusables]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      // A dialog opened from inside this one (e.g. "Add cohort value" over the
      // player form) owns Escape and Tab until it closes.
      const owner = (document.activeElement as HTMLElement | null)?.closest?.('[role="dialog"]');
      if (owner && owner !== panelRef.current) return;
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const elements = focusables();
      if (elements.length === 0) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [focusables, onClose]);

  return (
    <div
      className="overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className={`overlay__panel${panelClassName === undefined ? '' : ` ${panelClassName}`}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={panelRef}
        tabIndex={-1}
      >
        <div className="overlay__header">
          <span className="overlay__title">{title}</span>
          {headerExtra}
          <button type="button" className="overlay__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="overlay__scroll">{children}</div>
        {footer === undefined ? null : <div className="overlay__footer">{footer}</div>}
      </div>
    </div>
  );
}
